import APIService from './APIService';

class SearchService {
  /**
   * Global search across all types
   * @param {string} query - Search query
   * @param {number} take - Number of results per type
   * @param {number} skip - Number of results to skip
   * @returns {Promise<Object>} Search results by type
   */
  static async search(query, take = 10, skip = 0) {
    try {
      const response = await APIService.get(`/search?q=${encodeURIComponent(query)}&take=${take}&skip=${skip}`);
      const payload = response && response.data ? response.data : response;
      return this.parseSearchData(payload);
    } catch (error) {
      console.error('Error searching:', error);
      throw error;
    }
  }

  /**
   * Search users only
   * @param {string} query - Search query
   * @param {number} take - Number of results
   * @param {number} skip - Number to skip
   * @returns {Promise<Array>} User results
   */
  static async searchUsers(query, take = 10, skip = 0) {
    try {
      const response = await APIService.get(`/search/users?q=${encodeURIComponent(query)}&take=${take}&skip=${skip}`);
      return response && response.data ? response.data : response;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  /**
   * Get recent searches
   * @param {number} take - Number of results
   * @param {number} skip - Number to skip
   * @returns {Promise<Array>} Recent searches
   */
  static async getRecentSearches(take = 10, skip = 0) {
    try {
      const response = await APIService.get(`/search/recent?take=${take}&skip=${skip}`);
      return response && response.data ? response.data : response;
    } catch (error) {
      console.error('Error getting recent searches:', error);
      throw error;
    }
  }

  /**
   * Save a search query
   * @param {string} query - Search query to save
   * @param {Object|string} metadata - Optional metadata (or legacy `type` string)
   * @returns {Promise<Object>} Saved search data
   */
  static async saveSearch(query, metadata = null) {
    try {
      const data = { query };

      if (metadata && typeof metadata === 'object') {
        data.metadata = metadata;
      } else if (typeof metadata === 'string' && metadata.length) {
        // Backward compatibility with older callers that passed `type`
        data.metadata = { type: metadata };
      }
      
      const response = await APIService.post('/search/recent', data);
      return response && response.data ? response.data : response;
    } catch (error) {
      console.error('Error saving search:', error);
      throw error;
    }
  }

  /**
   * Clear all recent searches
   * @returns {Promise<boolean>} Success status
   */
  static async clearRecentSearches() {
    try {
      const response = await APIService.delete('/search/recent');
      const payload = response && response.data ? response.data : response;
      return payload?.message ? true : false;
    } catch (error) {
      console.error('Error clearing recent searches:', error);
      throw error;
    }
  }

  /**
   * Parse search data from API response
   * @param {Object} data - Raw search data
   * @returns {Object} Parsed search results
   */
  static parseSearchData(data) {
    const meta = data?.meta && typeof data.meta === 'object' ? data.meta : null;
    return {
      users: data.users || [],
      companies: data.companies || [],
      figures: data.figures || [],
      groups: data.groups || [],
      posts: data.posts || [],
      hashtags: data.hashtags || [],
      meta,
    };
  }
}

export default SearchService;
